import { Component, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import { ProductSelectorService } from './product-selector.service';
import { ProductInterface } from '@vendasta/marketplace-packages';
import { filter, map, take } from 'rxjs/operators';
import { ItemType, ListActivatableItemsFilters } from '@vendasta/marketplace-apps';
import { SelectedDropdownItem } from '@galaxy/inventory-ui';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '../../pipelines/tokens';

export interface ProductDialogData {
  products: ProductInterface[];
  partnerId: string;
  marketId: string;
}

interface CheckedProduct {
  checked: boolean;
  product: ProductInterface;
}

@Component({
  selector: 'sales-ui-product-selector',
  templateUrl: './product-selector.component.html',
  styleUrls: ['./product-selector.component.scss'],
})
export class ProductSelectorComponent {
  readonly products$: Observable<ProductInterface[]>;
  checkedProducts: CheckedProduct[];
  savingFilter$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  savingFilter$: Observable<boolean> = this.savingFilter$$.asObservable();

  itemSelectorFilters = new ListActivatableItemsFilters({
    itemTypes: [ItemType.ITEM_TYPE_APP],
  });
  selectedItems: SelectedDropdownItem[] = [];

  readonly userData$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
    map(([partnerId, marketId]) => {
      return {
        partnerId: partnerId,
        marketId: marketId,
      };
    }),
  );

  constructor(
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) readonly marketId$: Observable<string>,
    private readonly dialog: MatDialog,
    private readonly productsService: ProductSelectorService,
  ) {
    this.productsService.selectedProducts$.pipe(take(1)).subscribe((selectedProducts) => {
      this.selectedItems = selectedProducts.map((p) => ({ appId: p.productId, name: p.name }));
    });
    this.products$ = productsService.products$.pipe(filter((p) => !!p));
  }

  onSelectionChange(newSelection: SelectedDropdownItem[]): void {
    this.selectedItems = newSelection;
  }

  async saveItemSelection(): Promise<void> {
    this.savingFilter$$.next(true);
    const products = await firstValueFrom(this.products$);
    const productsToSearch = [];
    for (const product of products) {
      if (this.selectedItems.find((p) => p.appId === product.productId)) {
        productsToSearch.push(product);
      }
    }
    this.productsService.setProductSelection(productsToSearch);
    this.savingFilter$$.next(true);
    this.dialog.closeAll();
  }
}
